import { useState } from 'react';
import { ChevronDown, ChevronRight, Users } from 'lucide-react';
import { CHECKLIST_SECTIONS, normaliseProgress } from '@/components/AssemblyChecklist';
import { useChecklistEvents, useTechnicianNames } from '@/lib/api/team';
import { contributorIds, currentTicks, techLabel, unitContributions } from '@/lib/teamwork';

const ITEM_LABELS = new Map(CHECKLIST_SECTIONS.flatMap(s => s.items.map(i => [i.key, i.label] as const)));

interface AttributionOrder {
  id: string;
  quantity?: number | null;
  technician_id?: string | null;
  checklist_progress?: any;
  unit_builders?: Record<string, string> | null;
  submitted_by?: string | null;
  submitted_at?: string | null;
  tech_signed_by?: string | null;
}

/**
 * Who built each unit, who submitted and signed, and every checklist step
 * with the technician who ticked it - for reviewers and management. Steps are
 * recorded by the database from the signed-in account (see
 * 20261105170000_team_orders.sql); orders from before then show the assignee.
 */
export function BuildAttribution({ order }: { order: AttributionOrder }) {
  const [open, setOpen] = useState(false);
  const { data: events = [] } = useChecklistEvents(order.id);
  const unitCount = Math.max(order.quantity || 1, 1);
  const builders = order.unit_builders ?? {};

  const ids = [
    ...contributorIds(events),
    ...Object.values(builders),
    order.technician_id, order.submitted_by, order.tech_signed_by,
  ].filter((id): id is string => !!id);
  const { data: names } = useTechnicianNames(ids);
  const name = (id: string | null | undefined) => (id ? techLabel(names?.get(id)) : '—');

  if (!events.length && !order.submitted_by) return null;

  const progress = normaliseProgress(order.checklist_progress, unitCount);
  const ticks = currentTicks(events, progress);
  const signedBySomeoneElse = !!order.tech_signed_by && !!order.submitted_by && order.tech_signed_by !== order.submitted_by;

  return (
    <div className="rounded-lg border p-3 space-y-2 text-sm">
      <div className="flex items-center gap-2 font-medium">
        <Users className="h-4 w-4 text-muted-foreground" />
        Who did the work
      </div>

      {order.submitted_by && (
        <p className="text-xs text-muted-foreground">
          Submitted by <span className="font-medium text-foreground">{name(order.submitted_by)}</span>
          {order.submitted_at && <> on {new Date(order.submitted_at).toLocaleString()}</>}
          {' · '}
          {order.tech_signed_by
            ? signedBySomeoneElse
              ? <>signed by <span className="font-medium text-foreground">{name(order.tech_signed_by)}</span></>
              : 'signed by them'
            : 'not signed'}
        </p>
      )}

      <ul className="space-y-1">
        {Array.from({ length: unitCount }, (_, i) => {
          const unit = i + 1;
          const contribs = unitContributions(ticks, unit);
          const builder = builders[String(unit)] ?? order.technician_id;
          const others = contribs.filter(c => c.technicianId !== builder);
          const builderSteps = contribs.find(c => c.technicianId === builder)?.steps;
          return (
            <li key={unit} className="text-xs">
              {unitCount > 1 && <span className="text-muted-foreground">Unit {unit}: </span>}
              built by <span className="font-medium">{name(builder)}</span>
              {builderSteps != null
                ? <span className="text-muted-foreground"> ({builderSteps} step{builderSteps !== 1 ? 's' : ''})</span>
                : <span className="text-muted-foreground"> (assigned; steps not recorded)</span>}
              {others.length > 0 && (
                <span className="text-muted-foreground">
                  {' · also '}
                  {others.map(c => `${name(c.technicianId)} (${c.steps})`).join(', ')}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {events.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Step history ({events.length})
          </button>
          {open && (
            <ul className="mt-1 max-h-60 overflow-y-auto divide-y rounded border text-xs">
              {[...events].reverse().map(e => (
                <li key={e.id} className="flex gap-2 px-2 py-1">
                  <span className="text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</span>
                  <span className="font-medium whitespace-nowrap">{e.technician ? techLabel(e.technician) : 'System'}</span>
                  <span className="truncate">
                    {e.checked ? 'ticked' : 'unticked'} {ITEM_LABELS.get(e.item_key) ?? e.item_key}
                    {unitCount > 1 && <span className="text-muted-foreground"> · unit {e.unit_number}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
